import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { auth } from "../FirebaseConfig";

export const Popup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const popupRef = useRef(null);

  useEffect(() => {
    const isPageRefresh = window.performance.navigation.type === 1;
    if (isPageRefresh) {
      sessionStorage.removeItem("popupShownThisSession");
      localStorage.removeItem("popupShownThisSession");
    }
    if (location.pathname !== "/") {
      return;
    }
    const storedUser = localStorage.getItem("user");
    const currentUser = auth.currentUser;
    if (storedUser || currentUser) {
      return;
    }
    const popupShownThisSession = sessionStorage.getItem(
      "popupShownThisSession"
    );
    if (!popupShownThisSession) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setTimeout(() => setIsVisible(true), 50);
        sessionStorage.setItem("popupShownThisSession", "true");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => setIsOpen(false), 300);
  };

  const handleApply = () => {
    navigate("/freelancer-form");
    handleClose();
  };

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 flex items-center justify-center z-[9999] px-4"
        style={{
          background: isVisible
            ? "radial-gradient(circle at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)"
            : "transparent",
          backdropFilter: isVisible ? "blur(8px)" : "none",
        }}
      >
        <motion.div
          ref={popupRef}
          initial={{ scale: 0.75, y: 20, opacity: 0, rotate: 1 }}
          animate={{
            scale: isVisible ? 1 : 0.75,
            y: isVisible ? 0 : 20,
            opacity: isVisible ? 1 : 0,
            rotate: isVisible ? 0 : 1,
          }}
          exit={{ scale: 0.75, y: 20, opacity: 0, rotate: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white w-full max-w-5xl mx-4 relative flex flex-col lg:flex-row items-center rounded-3xl overflow-hidden"
          style={{
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 100px -12px rgba(28, 139, 227, 0.15)",
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          {/* Animated Background Elements */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none rounded-full"
            style={{ background: "linear-gradient(135deg, #373AC8, #ff6b35)" }}
            animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 left-0 w-24 h-24 opacity-10 pointer-events-none rounded-full"
            style={{ background: "linear-gradient(135deg, #ff6b35, #373AC8)" }}
            animate={{ y: [-5, 5, -5] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Close Button */}
          <motion.button
            onClick={handleClose}
            whileHover={{ scale: 1.1, rotate: 90, backgroundColor: "#373AC8" }}
            whileTap={{ scale: 0.9 }}
            className="absolute top-6 right-6 z-20 text-gray-400 rounded-full p-3 transition-all duration-300 focus:outline-none"
            style={{
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
            }}
            aria-label="Close popup"
          >
            <motion.div whileHover={{ color: "#ffffff" }}>
              <IoClose className="h-6 w-6" />
            </motion.div>
          </motion.button>

          {/* Image Section */}
          <div className="w-full lg:w-1/2 h-64 lg:h-[500px] relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-logo to-orange-500/20 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <img
              src="https://ajikasitsolutions.com/assets/img/hero/slider01.jpg"
              alt="AS-Tech Team"
              className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110"
              style={{
                filter: "brightness(1.1) contrast(1.05)",
              }}
            />
            {/* Floating Elements */}
            <div className="absolute top-8 left-8 w-4 h-4 rounded-full bg-logo animate-ping opacity-75" />
            <div className="absolute bottom-8 right-8 w-3 h-3 rounded-full bg-orange-400 animate-pulse" />
          </div>

          {/* Content Section */}
          <div className="w-full lg:w-1/2 text-center p-8 lg:p-12 relative z-10">
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isVisible ? 0 : 20, opacity: isVisible ? 1 : 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="text-3xl lg:text-5xl font-bold mb-2 tracking-tight bg-gradient-to-br from-logo to-[#ff6b35] bg-clip-text text-transparent"
              // style={{
              //   background: "linear-gradient(135deg, #373AC8 0%, #ff6b35 100%)",
              //   WebkitBackgroundClip: "text",
              //   WebkitTextFillColor: "transparent",
              // }}
            >
              Join the AS-Tech
            </motion.h2>
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isVisible ? 0 : 20, opacity: isVisible ? 1 : 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-3xl lg:text-5xl font-bold mb-6 tracking-tight bg-gradient-to-br from-logo to-[#ff6b35] bg-clip-text text-transparent"
              // style={{
              //   background: "linear-gradient(135deg, #ff6b35 0%, #373AC8 100%)",
              //   WebkitBackgroundClip: "text",
              //   WebkitTextFillColor: "transparent",
              // }}
            >
              Talent Pool! 🚀
            </motion.h2>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isVisible ? 0 : 20, opacity: isVisible ? 1 : 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="text-gray-600 mb-10 text-lg leading-relaxed max-w-md mx-auto"
            >
              Get featured in our exclusive interview series and get noticed by{" "}
              <span className="font-semibold text-gray-800">top employers</span>
              ! 💼
            </motion.p>
            <motion.button
              onClick={handleApply}
              whileHover={{
                scale: 1.05,
                boxShadow:
                  "0 15px 40px rgba(28, 139, 227, 0.4), 0 8px 25px rgba(255, 107, 53, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isVisible ? 0 : 20, opacity: isVisible ? 1 : 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="relative px-12 py-4 text-white font-semibold text-lg rounded-full overflow-hidden bg-gradient-to-br from-logo to-[#ff6b35]"
              // style={{
              //   background: "linear-gradient(135deg, #373AC8 0%, #ff6b35 100%)",
              //   boxShadow:
              //     "0 10px 30px rgba(28, 139, 227, 0.3), 0 5px 15px rgba(255, 107, 53, 0.2)",
              // }}
            >
              <span className="relative z-10 tracking-wide">Apply Now</span>
              <motion.div
                className="absolute inset-0 bg-white opacity-0"
                whileHover={{ opacity: 0.1 }}
                transition={{ duration: 0.3 }}
              />
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-white opacity-0"
                whileHover={{ opacity: 0.3 }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.button>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isVisible ? 0 : 20, opacity: isVisible ? 1 : 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-8 flex items-center justify-center space-x-6 text-sm text-gray-500"
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <span className="font-medium">100% Free</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-logo"></div>
                <span className="font-medium">Instant Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                <span className="font-medium">No Spam</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
